import type { RequiredGeneric } from '@pba/utils';
import { useTranslation } from 'react-i18next';
import { Col, Fieldset, Form, Row, SelectInput, TextInput } from 'ui';
import { countries, states } from 'common/utils';

export type AddressFormValues = {
  apt?: string;
  city?: string;
  country?: string;
  state?: string;
  street?: string;
  zip?: string;
};

type P = {
  isRequired?: boolean;
};

const AddressFieldset = <FormValues extends AddressFormValues = never>({
  isRequired,
}: RequiredGeneric<FormValues, P>) => {
  const { t: translationCountry } = useTranslation('translationCountries');
  const { t: translationState } = useTranslation('translationStates');

  return (
    <Fieldset legend={{ title: 'Home Address' }}>
      <Row gutter="xs">
        <Col span={12}>
          <TextInput
            input={{
              autoComplete: 'address-line1',
            }}
            item={{
              label: 'Street',
              name: 'street',
              rules: [
                {
                  required: isRequired,
                },
              ],
            }}
          />
        </Col>
        <Col span={12}>
          <TextInput
            input={{
              autoComplete: 'address-line2',
            }}
            item={{
              label: 'Apartment',
              name: 'apt',
            }}
          />
        </Col>
        <Col span={24}>
          <TextInput
            input={{
              autoComplete: 'address-level2',
            }}
            item={{
              label: 'City',
              name: 'city',
              rules: [
                {
                  required: isRequired,
                },
              ],
            }}
          />
        </Col>
        <Form.Item<AddressFormValues> dependencies={['country']} noStyle>
          {({ getFieldValue }) =>
            getFieldValue('country') === 'US' ? (
              <Col span={24}>
                <SelectInput
                  input={{
                    getOptionLabel: (option) => translationState(option.translationKey),
                    getOptionValue: (option) => option.code,
                    options: states,
                    showSearch: true,
                  }}
                  item={{
                    label: 'State',
                    name: 'state',
                    rules: [
                      {
                        required: isRequired,
                      },
                    ],
                  }}
                />
              </Col>
            ) : null
          }
        </Form.Item>
        <Col span={24}>
          <TextInput
            input={{
              autoComplete: 'postal-code',
            }}
            item={{
              label: 'ZIP code',
              name: 'zip',
              rules: [
                {
                  required: isRequired,
                },
              ],
            }}
          />
        </Col>
        <Col span={24}>
          <SelectInput
            input={{
              getOptionLabel: (option) => translationCountry(option.translationKey),
              getOptionValue: (option) => option.code,
              options: countries,
              showSearch: { optionFilterProp: 'label' },
            }}
            item={{
              label: 'Country',
              name: 'country',
              rules: [
                {
                  required: isRequired,
                },
              ],
            }}
          />
        </Col>
      </Row>
    </Fieldset>
  );
};

export default AddressFieldset;
